import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAllStates } from "http-hooks/state/usestate";
import {
  useCreatePartySupMast,
  useOnePartySupMast,
  useUpdatePartySupMast,
} from "http-hooks/partysupmast/usepartysupmast";
import { useAllAreas } from "http-hooks/area/useArea";
import { useAllCountries } from "http-hooks/country/useCountry";
import useCompanyPreferences from "hooks/useCompanyPreferences";

export const accountTypes = [
  { value: "C", label: "Customer" },
  { value: "V", label: "Vendor" },
  { value: "T", label: "Transporter" },
  { value: "A", label: "Agent" },
  { value: "S", label: "Salesman" },
];

const initialFormData = {
  ps_code: "",
  agvct: "",
  name: "",
  stateid: "",
  ps_country: "IN",
  ps_state: "",
  select_grptype: "",
  tcsperc: 0,
  transaction_limit: 0,
  gst: true,
  psadd1: "",
  psadd2: "",
  psadd3: "",
  ps_city: "",
  ps_pin: 0,
  ps_contactperson: "",
  ps_contactno: "",
  ps_contactres: "",
  ps_mobile1: "",
  ps_mobile2: "",
  ps_wtsno: "",
  ps_emailid: "",
  ps_emailid1: "",
  ps_gstno: "",
  ps_panno: "",
  tptcode: "",
  sales_contactnowts: "",
  sales_email: "",
  sales_email1: "",
  acc_contactnowts: "",
  acc_email: "",
  acc_email1: "",
  scheme_contactnowts: "",
  ps_scheme_order_email: "",
  boolcustlogin: false,
  allow_group_access: false,
  boolsendmail: false,
  areaId: null,
};

const getPartyFormData = (master, detail = {}, psCode = "") => ({
  ...initialFormData,
  ps_code: psCode,
  agvct: master.type || master.agvct || "",
  name: master.name || "",
  stateid: detail.stateid ?? master.stateid ?? "",
  ps_country: (detail.ps_country || master.ps_country || "IN").toUpperCase(),
  ps_state: detail.ps_state || master.ps_state || "",
  select_grptype: master.grpId || master.select_grptype || "",
  tcsperc: master.tcsperc || 0,
  transaction_limit: master.transaction_limit || 0,
  gst: master.gst !== undefined ? master.gst : true,
  psadd1: detail.psadd1 || master.psadd1 || "",
  psadd2: detail.psadd2 || master.psadd2 || "",
  psadd3: detail.psadd3 || master.psadd3 || "",
  ps_city: detail.ps_city || master.ps_city || "",
  ps_pin: detail.ps_pin || master.ps_pin || 0,
  ps_contactperson: detail.ps_contactperson || master.ps_contactperson || "",
  ps_contactno: detail.ps_contactno || master.ps_contactno || "",
  ps_contactres: detail.ps_contactres || master.ps_contactres || "",
  ps_mobile1: detail.ps_mobile1 || master.ps_mobile1 || "",
  ps_mobile2: detail.ps_mobile2 || master.ps_mobile2 || "",
  ps_wtsno: detail.ps_wtsno || master.ps_wtsno || "",
  ps_emailid: detail.ps_emailid || master.ps_emailid || "",
  ps_emailid1: detail.ps_emailid1 || master.ps_emailid1 || "",
  ps_gstno: detail.ps_gstno || master.ps_gstno || "",
  ps_panno: detail.ps_panno || master.ps_panno || "",
  tptcode: detail.tptcode || master.tptcode || "",
  sales_contactnowts: detail.sales_contactnowts || master.sales_contactnowts || "",
  sales_email: detail.sales_email || master.sales_email || "",
  sales_email1: detail.sales_email1 || master.sales_email1 || "",
  acc_contactnowts: detail.acc_contactnowts || master.acc_contactnowts || "",
  acc_email: detail.acc_email || master.acc_email || "",
  acc_email1: detail.acc_email1 || master.acc_email1 || "",
  scheme_contactnowts: detail.scheme_contactnowts || master.scheme_contactnowts || "",
  ps_scheme_order_email:
    detail.ps_scheme_order_email || master.ps_scheme_order_email || "",
  boolcustlogin: detail.boolcustlogin || master.boolcustlogin || false,
  allow_group_access:
    detail.allow_group_access || master.allow_group_access || false,
  boolsendmail: detail.boolsendmail || master.boolsendmail || false,
  areaId: master.areaId || null,
});

export default function useManageGLAccountMastForm({
  ps_Id,
  initialData,
  onSubmit,
  externalLoading,
}) {
  const hasInitialized = useRef(false);
  const [formData, setFormData] = useState(initialFormData);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [showDetails, setShowDetails] = useState(Boolean(ps_Id));

  const { isForeignExportEnabled } = useCompanyPreferences();
  const isEditMode = Boolean(ps_Id);
  const isForeignExport = isForeignExportEnabled();
  const isIndianAddress = !isForeignExport || formData.ps_country === "IN";
  const isForeignAddress = isForeignExport && !isIndianAddress;

  const { data: existingData, isLoading: isLoadingExisting } =
    useOnePartySupMast(ps_Id);
  const { data: statesData = [], isLoading: isStatesLoading } = useAllStates();
  const { data: countriesData = [], isLoading: isCountriesLoading } =
    useAllCountries(isForeignExport);
  const { data: { data: areasData = [] } = {} } = useAllAreas(
    { ag_status: 1, noPagination: true },
    formData.agvct === "C"
  );
  const createMutation = useCreatePartySupMast();
  const updateMutation = useUpdatePartySupMast();

  const stateOptions = useMemo(
    () =>
      statesData?.data?.map((state) => ({
        value: state.stateId,
        label: state.stateName,
      })) || [],
    [statesData]
  );
  const countryOptions = useMemo(
    () =>
      countriesData.map((country) => ({
        value: country.code,
        label: `${country.name} (${country.code})`,
      })),
    [countriesData]
  );

  const setSelectedOptions = useCallback((master, detail = {}) => {
    const type = master.type || master.agvct;
    setSelectedType(accountTypes.find((option) => option.value === type) || null);

    const stateid = detail.stateid ?? master.stateid;
    setSelectedState(
      stateOptions.find((option) => option.value === stateid) || null
    );

    if (master.areaId && type === "C") {
      setSelectedArea(
        areasData.find((area) => area.areaId === master.areaId) || null
      );
    }
  }, [areasData, stateOptions]);

  useEffect(() => {
    if (!isEditMode || !existingData) return;

    const detail = existingData.partySupDetail || {};
    const psCode = existingData.ps_Id?.substring(1) || "";
    setFormData(getPartyFormData(existingData, detail, psCode));
    setSelectedOptions(existingData, detail);
  }, [existingData, isEditMode, setSelectedOptions]);

  useEffect(() => {
    if (isEditMode || !initialData || hasInitialized.current) return;

    setFormData(getPartyFormData(initialData, initialData, initialData.ps_code || ""));
    setSelectedOptions(initialData, initialData);
    hasInitialized.current = true;
  }, [initialData, isEditMode, setSelectedOptions]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleTypeChange = (typeOption) => {
    setSelectedType(typeOption);
    setSelectedArea(null);
    setFormData((previous) => ({
      ...previous,
      agvct: typeOption?.value || "",
      select_grptype: "",
      areaId: null,
    }));
  };

  const handleStateChange = (stateOption) => {
    setSelectedState(stateOption);
    setFormData((previous) => ({
      ...previous,
      stateid: stateOption?.value || "",
    }));
  };

  const handleCountryChange = (countryOption) => {
    const ps_country = countryOption?.value || "IN";
    const isIndia = ps_country === "IN";

    setFormData((previous) => ({
      ...previous,
      ps_country,
      stateid: isIndia ? previous.stateid : 0,
      ps_state: isIndia ? "" : previous.ps_state,
      ps_gstno: isIndia ? previous.ps_gstno : "",
      ps_panno: isIndia ? previous.ps_panno : "",
      tcsperc: isIndia ? previous.tcsperc : 0,
      gst: isIndia ? previous.gst : false,
    }));

    if (!isIndia) setSelectedState(null);
  };

  const handleAreaChange = (areaOption) => {
    setSelectedArea(areaOption);
    setFormData((previous) => ({
      ...previous,
      areaId: areaOption?.areaId || null,
    }));
  };

  const handleGSTToggle = (enabled) => {
    setFormData((previous) => ({ ...previous, gst: enabled }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const submitData = {
      ...formData,
      gst: isForeignAddress ? 0 : formData.gst ? 1 : 0,
      ps_gstno: isForeignAddress ? "" : formData.ps_gstno,
      ps_panno: isForeignAddress ? "" : formData.ps_panno,
      tcsperc: isForeignAddress ? 0 : formData.tcsperc,
      boolcustlogin: formData.boolcustlogin ? 1 : 0,
      allow_group_access: formData.allow_group_access ? 1 : 0,
      boolsendmail: formData.boolsendmail ? 1 : 0,
    };

    if (isEditMode) {
      delete submitData.ps_code;
      delete submitData.agvct;
    }

    try {
      if (isEditMode) {
        await updateMutation.mutateAsync({ id: ps_Id, data: submitData });
      } else {
        await createMutation.mutateAsync(submitData);
      }
      onSubmit?.();
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const isMutating = createMutation.isPending || updateMutation.isPending;

  return {
    formData,
    setFormData,
    selectedType,
    selectedState,
    selectedArea,
    isEditMode,
    isForeignExport,
    isIndianAddress,
    isForeignAddress,
    showDetails,
    setShowDetails,
    stateOptions,
    countryOptions,
    isCountriesLoading,
    isStatesLoading,
    isLoadingExisting,
    isLoading: isMutating || externalLoading || isLoadingExisting,
    isMutating,
    handleInputChange,
    handleTypeChange,
    handleStateChange,
    handleCountryChange,
    handleAreaChange,
    handleGSTToggle,
    handleSubmit,
  };
}
